import type { AlertColor } from "@mui/material/Alert/Alert";
import { AxiosError } from "axios";

import type QueryClient from "../api/query/queryClient";
import { ReviewStatus } from "../status";
import { isoNow } from "../utils/utils";

export async function validateClaimAndNotify(
  claim: ClaimWithProcedureAndPatientMessage,
  updateStore: (action: {
    type: "UPDATE_CLAIM";
    claimToUpdate: ClaimWithProcedureAndPatientMessage;
  }) => void,
  queryClient: QueryClient,
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void,
) {
  try {
    updateStore({
      type: "UPDATE_CLAIM",
      claimToUpdate: {
        ...claim,
        reviewStatus: ReviewStatus[ReviewStatus.VALIDATING],
        updatedAt: isoNow(),
      },
    });
    const newReviewStatus = await queryClient.validateEntity(
      claim.wieldyId,
      "Claim",
    );
    updateStore({
      type: "UPDATE_CLAIM",
      claimToUpdate: {
        ...claim,
        reviewStatus: newReviewStatus,
        updatedAt: isoNow(),
      },
    });
    if (newReviewStatus === ReviewStatus.REVIEW) {
      updateSnackBar({
        severity: "error",
        message: `${claim.wieldyId}: Claim is still in REVIEW`,
      });
      return;
    }
    updateSnackBar({
      severity: "success",
      message: `${claim.wieldyId}: Successfully validated claim`,
    });
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.data) {
      const responseBody = error.response.data;
      updateSnackBar({
        severity: "error",
        message:
          responseBody.detail?.[0]?.msg ||
          "An unknown error occurred. Reach out to engineering.",
      });
    }
    updateStore({
      type: "UPDATE_CLAIM",
      claimToUpdate: {
        ...claim,
        reviewStatus: ReviewStatus[ReviewStatus.REVIEW],
        updatedAt: isoNow(),
      },
    });
  }
}

export async function validatePaymentAndNotify(
  payment: PaymentMessage,
  updateStore: (action: {
    type: "UPDATE_PAYMENT";
    paymentToUpdate: PaymentMessage;
  }) => void,
  queryClient: QueryClient,
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void,
) {
  try {
    updateStore({
      type: "UPDATE_PAYMENT",
      paymentToUpdate: {
        ...payment,
        reviewStatus: ReviewStatus[ReviewStatus.VALIDATING],
      },
    });
    const newReviewStatus = await queryClient.validateEntity(
      payment.wieldyId,
      "Payment",
    );
    updateStore({
      type: "UPDATE_PAYMENT",
      paymentToUpdate: {
        ...payment,
        reviewStatus: newReviewStatus,
      },
    });
    if (newReviewStatus === ReviewStatus.REVIEW) {
      updateSnackBar({
        severity: "error",
        message: `${payment.wieldyId}: Payment is still in REVIEW`,
      });
      return;
    }
    updateSnackBar({
      severity: "success",
      message: `${payment.wieldyId}: Successfully validated payment`,
    });
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.data) {
      const responseBody = error.response.data;
      updateSnackBar({
        severity: "error",
        message:
          responseBody.detail?.[0]?.msg ||
          "An unknown error occurred. Reach out to engineering.",
      });
    }
    updateStore({
      type: "UPDATE_PAYMENT",
      paymentToUpdate: {
        ...payment,
        reviewStatus: ReviewStatus[ReviewStatus.REVIEW],
      },
    });
  }
}
